import { ReactNode, createContext, useContext, useMemo, useState, useEffect } from 'react';
import { IProfileViewModel } from 'viewModels';
import { useQuery } from 'react-query';
import { CompanyController } from '../controller/CompanyController';
import { useAuth } from './auth';
import useWebSocket from '../hooks/useWebSocket';
import { TypeUser } from '../types/enums';

type ProfileContextProps = {
  children: ReactNode;
};
interface IInitialValues {
  profiles: IProfileViewModel[];
  setProfiles: React.Dispatch<React.SetStateAction<IProfileViewModel[]>>;
  isLoadingProfiles: boolean;
}
const ProfileContext = createContext({} as IInitialValues);

const ProfileContextProvider = ({ children }: ProfileContextProps) => {
  const [profiles, setProfiles] = useState<IProfileViewModel[]>([]);
  const { user, tokenIsValid } = useAuth();

  const { isLoading: isLoadingProfiles } = useQuery(
    'profiles',
    () => CompanyController.getProfilesByUserId(user.id),
    {
      onSuccess: (data) => {
        setProfiles(data);
      },
      refetchOnWindowFocus: false,
      enabled: !!user.id && tokenIsValid && [TypeUser.COMPANY, TypeUser.ADM].includes(user.type),
    },
  );

  useWebSocket<IProfileViewModel>({
    url: `profile/${user.id}/update-profile`,
    onMessage: (profile) => {
      setProfiles((prev) => {
        const index = prev.findIndex((c) => c.id === profile.id);
        if (index !== -1) {
          return [...prev.slice(0, index), profile, ...prev.slice(index + 1)];
        }
        return [...prev];
      });
    },
  });

  useWebSocket<string>({
    url: `profile/${user.id}/remove-profile`,
    onMessage: (profileId) => {
      setProfiles(() => {
        const newProfiles = profiles.filter((profile) => profile.id !== profileId);
        return newProfiles;
      });
    },
  });

  useEffect(() => {
    if (!tokenIsValid) {
      setProfiles([]);
    }
  }, [tokenIsValid]);

  const contextValues: IInitialValues = useMemo(
    () => ({
      profiles,
      setProfiles,
      isLoadingProfiles,
    }),
    [profiles, setProfiles, isLoadingProfiles],
  );
  return <ProfileContext.Provider value={contextValues}>{children}</ProfileContext.Provider>;
};
export const useProfile = () => useContext(ProfileContext);
export default ProfileContextProvider;
